import apiClient from './api/config';
import { getWithAuth, postWithAuthLocal } from './api/client';
import type { ApiResponse, CreateGroupRequest, GroupDto, PagedRequest, PagedResult } from '../types/groups';

const GROUP_BASE_URL = '/app/Group';
const MESSAGES_BASE_URL = '/app/messages/group/send';
const UPLOAD_ATTACHMENT_URL = '/app/messages/group/upload-attachment';

export interface SendGroupMessageRequest {
  groupId: number;
  conversationId: number;
  messageText: string;
  messageType: string;
  attachmentUrl?: string | null;
}

export interface ChatAttachmentUploadResult {
  attachmentUrl: string;
  fileName: string;
  fileType: string;
  fileSize: number;
}

export const getPagedGroups = async (request: PagedRequest): Promise<PagedResult<GroupDto>> => {
  const response = await getWithAuth<ApiResponse<PagedResult<GroupDto>>>(`${GROUP_BASE_URL}/paged`, {
    start: request.start,
    length: request.length,
    orderColumn: request.orderColumn,
    orderDirection: request.orderDirection,
    search: request.search ?? '',
    groupType: request.groupType,
  });

  if (!response.success) {
    throw new Error(response.message || 'Unable to load groups.');
  }

  return response.data;
};

export const getGroupById = async (groupId: number): Promise<GroupDto> => {
  const response = await getWithAuth<ApiResponse<GroupDto>>(`${GROUP_BASE_URL}/${groupId}`);

  if (!response.success) {
    throw new Error(response.message || 'Unable to load group.');
  }

  return response.data;
};

export const createGroup = async (payload: CreateGroupRequest): Promise<GroupDto> => {
  const response = await postWithAuthLocal<ApiResponse<GroupDto>>(GROUP_BASE_URL, payload);

  if (!response.success) {
    throw new Error(response.message || 'Unable to create group.');
  }

  return response.data;
};

export const removeGroupMember = async (groupId: number, userId: string) => {
  return apiClient.delete(`${GROUP_BASE_URL}/${groupId}/members/${userId}`);
};

export const deleteGroup = async (groupId: number) => {
  return apiClient.delete(`${GROUP_BASE_URL}/${groupId}`);
};

export const sendGroupMessage = async (payload: SendGroupMessageRequest): Promise<void> => {
  const response = await postWithAuthLocal<ApiResponse<void>>(MESSAGES_BASE_URL, payload);

  if (!response.success) {
    throw new Error(response.message || 'Unable to send message.');
  }
};

export const uploadGroupAttachment = async (file: File): Promise<ChatAttachmentUploadResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await postWithAuthLocal<ApiResponse<ChatAttachmentUploadResult>>(UPLOAD_ATTACHMENT_URL, formData);

  if (!response.success) {
    throw new Error(response.message || 'Unable to upload attachment.');
  }

  return response.data;
};
